import { useEffect, useState } from "react";
import FaqMarkerOverlay from "./FaqMarkerOverlay";
import useFaqT from "./useFaqT";
import { FAQ_MEDIA_SETS, faqImagePath } from "../../constants/faq/faqConfig";
import { fetchFaqAssetUrl, fetchFaqMarkers } from "../../services/faqAssetService";

// Media slot (design F1/F7/F8-B): 16:10 desktop set / 9:16 mobile set.
// Placeholder state = striped frame + slot key (the brief's file-name
// contract) + size note, shown until the auth-protected file exists (T3).
// Loaded state = the clean screenshot. The marker overlay renders on top in
// BOTH states — coordinates only, never burned in.
//
// T3: the slot asks the auth-protected asset endpoint for its file (Bearer
// fetch → blob URL — a plain <img src> can't carry the token; the assets are
// never public, TP_FAQ_BRIEF §5). A 404 simply keeps the placeholder state,
// so shipping an image later needs no code change — the adaptability core.
const FaqMediaSlot = ({ question, set, activeN, onActivate, onDeactivate, onTap }) => {
  const tr = useFaqT();
  const media = question.media && question.media[set];
  const slotKey = media ? faqImagePath(question.tier, question.category, question.id, set) : null;

  // {src, markers} once the protected asset exists; null → placeholder.
  const [loaded, setLoaded] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoaded(null);
    if (slotKey && !(media && media.src)) {
      fetchFaqAssetUrl(slotKey).then(async (src) => {
        if (!src) return;
        const markerJson = await fetchFaqMarkers(slotKey);
        if (alive) setLoaded({ src, markers: markerJson });
      });
    }
    return () => {
      alive = false;
    };
  }, [slotKey, media]);

  if (!media) return null;
  const geo = FAQ_MEDIA_SETS[set];
  const src = media.src || (loaded && loaded.src) || null;
  // A real rendered asset brings its OWN marker JSON (written by the
  // pipeline from real bounding boxes); placeholder markers only accompany
  // the placeholder state.
  const markers = loaded ? loaded.markers : media.markers;

  return (
    <div
      data-testid={`faq-media-${set}`}
      className="relative w-full border border-[#C8CCD3] bg-[#E8EAED]"
      style={{
        aspectRatio: geo.ratio,
        backgroundImage: src
          ? undefined
          : "repeating-linear-gradient(135deg, rgba(255,255,255,.55) 0 8px, rgba(255,255,255,0) 8px 16px)",
      }}
    >
      {src ? (
        // The alt text is composed here, not read off `media.alt`: the data
        // layer builds that string by concatenating an English prefix with the
        // raw EN question (faqStructure.js:98), so a screen reader in any of the
        // other 15 languages got a fully English sentence — invisible until the
        // first images shipped (control pass B-20, same class as B-09). The
        // prefix is chrome copy and belongs in the chrome bundle; the question
        // itself is already a translated key, so nothing is translated twice.
        <img
          src={src}
          alt={
            set === "mobile"
              ? tr("Annotated mobile screenshot: {{question}}", { question: tr(question.q) })
              : tr("Annotated screenshot: {{question}}", { question: tr(question.q) })
          }
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-4 text-center">
          <span
            aria-hidden="true"
            className={`block border-2 border-[#9AA1AC] ${set === "mobile" ? "h-[30px] w-[22px]" : "h-[22px] w-[30px]"}`}
          />
          <span className="break-all font-faqmono text-[11px] text-[#5A626E] md:text-[12px]">{slotKey}</span>
          <span className="font-faqmono text-[10px] text-[#8A919D]">{geo.sizeNote}</span>
        </div>
      )}
      <FaqMarkerOverlay
        markerJson={markers}
        activeN={activeN}
        onActivate={onActivate}
        onDeactivate={onDeactivate}
        onTap={onTap}
      />
    </div>
  );
};

export default FaqMediaSlot;
